import { memo, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { useIntl } from 'react-intl';
import { Text } from '@library';
import CollapsibleContent from '../../../core/components/CollapsibleContent';
import useSearchParamFilterHelper from '../../hooks/useSearchParamFilterHelper';
import useProductFilterCount from '../../queries/useProductFilterCount';
import notEmpty from '../../../core/util/notEmpty';
import searchInFields from '../../../core/util/searchInFields';
import CollapsibleSideBarFilterTitle from '../ProductList/Filters/CollapsibleSideBarFilterTitle';
import withErrorBoundary from '../../../core/components/errors/withErrorBoundary';
import FilterShowMore from './FilterShowMore';
import { FiltersSearchField } from './FilterWrapper';
import styles from './FilterSize.module.scss';

const filterSizeValues = ({
    filterValues,
    search,
}: {
    filterValues: {
        value: string;
        valueLabel?: string;
        total: number;
    }[];
    search: string;
}): [{ value: string; valueLabel?: string }[], number, number] => {
    const cleanedValues = filterValues
        .filter(({ value }) => value !== '')
        .map(({ value, valueLabel, total }) => {
            if (!total) {
                return null;
            }

            return { value, valueLabel };
        })
        .filter(notEmpty);

    const filteredValues = cleanedValues.filter(({ value, valueLabel }) => {
        const fieldsToSearch = [valueLabel || value].join().toLowerCase();
        return searchInFields(fieldsToSearch, search);
    });

    return [filteredValues, cleanedValues.length, filteredValues.length];
};

const FilterSize = withErrorBoundary(
    memo(
        ({
            name,
            field,
            defaultExpand = true,
        }: {
            name: string;
            field: string;
            defaultExpand?: boolean;
        }) => {
            const [expand, setExpand] = useState(defaultExpand);

            const expanded = (isExpanded: boolean) => {
                setExpand(isExpanded);
            };
            useEffect(() => {
                setExpand(defaultExpand);
            }, [defaultExpand]);

            const [search, setSearch] = useState('');
            const { productFilterValues } = useProductFilterCount({
                filterField: field,
            });

            const [filteredValues, count] = filterSizeValues({
                filterValues: productFilterValues,
                search,
            });

            if (count === 0) {
                return null;
            }

            return (
                <CollapsibleContent
                    onExpand={expanded}
                    expanded={expand}
                    title={
                        <CollapsibleSideBarFilterTitle>
                            {name}
                        </CollapsibleSideBarFilterTitle>
                    }
                    endArrow
                    filterIcon
                >
                    {expand && (
                        <>
                            <FilterContent
                                name={name}
                                field={field}
                                values={productFilterValues}
                                filteredValues={filteredValues}
                                setSearch={setSearch}
                            />
                        </>
                    )}
                </CollapsibleContent>
            );
        }
    )
);

const FilterContent = memo(
    ({
        name,
        field,
        values,
        filteredValues,
        setSearch,
    }: {
        name: string;
        field: string;
        values: { value: string; valueLabel?: string }[];
        filteredValues: { value: string; valueLabel?: string }[];
        setSearch: React.Dispatch<React.SetStateAction<string>>;
    }) => {
        const intl = useIntl();
        const { selectedValues, updateCurrentValues } =
            useSearchParamFilterHelper()(field);
        const [showFullList, setShowFullList] = useState(false);

        return (
            <>
                {values.length > 12 && (
                    <div className={styles.searchField}>
                        <FiltersSearchField
                            setSearch={setSearch}
                            placeholder={`${intl.formatMessage({
                                id: 'search_verb',
                            })}`}
                        />
                    </div>
                )}
                {selectedValues.length > 0 && (
                    <div className={clsx(styles.values, styles.selectedValues)}>
                        {values
                            .filter(item => selectedValues.includes(item.value))
                            .map(({ value, valueLabel }) => (
                                <FilterSizeItem
                                    key={`size-filter-selected-${value}`}
                                    name={name}
                                    value={value}
                                    label={valueLabel}
                                    selectedValues={selectedValues}
                                    updateCurrentValues={updateCurrentValues}
                                />
                            ))}
                    </div>
                )}
                <div className={styles.values}>
                    {filteredValues
                        .filter(({ value }) => !selectedValues.includes(value))
                        .slice(0, showFullList ? undefined : 12)
                        .map(({ value, valueLabel }) => (
                            <FilterSizeItem
                                key={`size-filter-${value}`}
                                name={name}
                                value={value}
                                label={valueLabel}
                                selectedValues={selectedValues}
                                updateCurrentValues={updateCurrentValues}
                            />
                        ))}
                </div>
                {filteredValues.length > 12 && (
                    <FilterShowMore
                        showFullList={showFullList}
                        setShowFullList={setShowFullList}
                        remainingItems={filteredValues.length - 12}
                    />
                )}
            </>
        );
    }
);

const FilterSizeItem = memo(
    ({
        name,
        value,
        label,
        selectedValues,
        updateCurrentValues,
    }: {
        name: string;
        value: string;
        label?: string;
        selectedValues: string[];
        updateCurrentValues(
            value: string,
            isRemove?: boolean,
            isReplace?: boolean
        ): void;
    }) => {
        return (
            <div
                key={`${name} ${value}`}
                role="button"
                tabIndex={0}
                className={clsx(styles.productSizeItem, {
                    [styles.productSizeItemSelected]:
                        selectedValues.includes(value),
                })}
                onClick={() =>
                    updateCurrentValues(value, selectedValues.includes(value))
                }
                onKeyPress={(e: React.KeyboardEvent<HTMLDivElement>) => {
                    if ([' ', 'Enter'].includes(e.key)) {
                        updateCurrentValues(
                            value,
                            selectedValues.includes(value)
                        );
                    }
                }}
            >
                <Text
                    center
                    secondary
                    regular
                    bold={selectedValues.includes(value)}
                >
                    {label || value}
                </Text>
            </div>
        );
    }
);

export default FilterSize;
